import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import { AuthContext } from '../components/AuthProvider';
import {
    get_users,
    get_user_details,
    update_user,
    delete_user
} from "../tools-network";

import { showTemporaryMessage } from "../components/Message";

const UserDetails = ({ loginToken, loggedInUserId, loggedInUserType, selectedUserId, refreshUserList }) => {
    const { t } = useTranslation();

    const { handleInvalidToken } = React.useContext(AuthContext);

    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [updatePasswordRegularEnabled, setUpdatePasswordRegularEnabled] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [isGoogleUser, setIsGoogleUser] = useState(false);
    const [isBankIdUser, setIsBankIdUser] = useState(false);

    const userTypes = ['REGULAR', 'ADMIN', 'SUPERUSER', 'PRINTER'];
    const [userType, setUserType] = useState('REGULAR');

    const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
    const [submitGuiEnabled, setSubmitGuiEnabled] = useState(true);

    const [currentPassword, setCurrentPassword] = useState('');
    const [updatePasswordEnabledForRegularUser, setUpdatePasswordEnabledForRegularUser] = useState(false);

    const [userData, setUserData] = useState(null);
    const updateUserDetails = async () => {
        try {
            const res = await get_user_details(loginToken, selectedUserId);
            if (res.status) {
                const userDetails = res.data;
                console.log("USER DATA: " + JSON.stringify(userDetails));
                setUserData(userDetails);
            } else {
                showTemporaryMessage("Fetch users FAILED!", res.msg, 'danger');
                handleInvalidToken();
            }
        } catch (err) {
            showTemporaryMessage("Fetch users FAILED!", err, 'danger');
            handleInvalidToken();
        }
    };

    useEffect(() => {
        if (name) {
            setIsGoogleUser(name.includes('@'));
            setIsBankIdUser(/^\d+$/.test(name));
        } else {
            setIsGoogleUser(false);
            setIsBankIdUser(false);
        }
    }, [name]);

    useEffect(() => {
        updateUserDetails();
    }, [selectedUserId]);

    useEffect(() => {
        setName(userData?.name ?? '');
        setUserType(userData?.type ?? 'REGULAR');
        setIsEnabled(!!userData?.isenabled);
    }, [userData]);

    useEffect(() => {
        if (userData && userData.name !== undefined) {
            let changed = false;
            if (userData.name !== name) {
                changed = true;
            }
            if (userData.type !== userType) {
                changed = true;
            }
            if (userData.isenabled !== isEnabled) {
                changed = true;
            }
            if (password !== '') {
                changed = true;
            }
            setSubmitButtonEnabled(changed);
        }
    }, [userData, name, password, userType, isEnabled]);

    useEffect(() => {
        setUpdatePasswordRegularEnabled(password === passwordConfirm && password !== "");
    }, [password, passwordConfirm]);

    const isAdminUser = (loggedInUserType === "ADMIN" || loggedInUserType === "SUPERUSER");

    function disableUpdateUser() {
        setSubmitGuiEnabled(false);
    }

    function enableUpdateUser() {
        setSubmitGuiEnabled(true);
    }

    async function submitUpdate() {
        disableUpdateUser();
        const request_body = {};
        if (userData.name !== name) {
            request_body.name = name;
        }
        if (userData.type !== userType) {
            request_body.type = userType;
        }
        if (userData.isenabled !== isEnabled) {
            request_body.isenabled = isEnabled;
        }
        if (password !== '') {
            request_body.password = password;
            setPassword('');
            setPasswordConfirm('');
        }
        if (Object.keys(request_body).length === 0) {
            enableUpdateUser();
            return;
        }

        try {
            const res = await update_user(loginToken, userData.id, request_body);
            if (res.success) {
                showTemporaryMessage(t("user_information_successfully_updated"), " ", 'success');
                enableUpdateUser();
                if (request_body.name) {
                    refreshUserList?.();
                }
                updateUserDetails();
            } else {
                showTemporaryMessage("Update user FAILED!", res.msg, 'danger');
                enableUpdateUser();
            }
        } catch (err) {
            showTemporaryMessage("Update user FAILED!", err, 'danger');
            enableUpdateUser();
        }
    }

    async function deleteUser() {
        if (!isAdminUser) {
            showTemporaryMessage("Not allowed to delete users!", " ", 'danger');
            return;
        }

        const deletingOurselves = userData.id === loggedInUserId;
        if (deletingOurselves) {
            console.log("TRYING TO DELETE OURSELVES!!!");
        }

        try {
            const res = await delete_user(loginToken, userData.id);
            if (res.success) {
                showTemporaryMessage(t("user_successfully_deleted"), " ", 'success');
                enableUpdateUser();
                refreshUserList?.();
            } else {
                showTemporaryMessage("Delete user FAILED: ", res.msg, 'danger');
                enableUpdateUser();
            }
        } catch (err) {
            showTemporaryMessage("Delete user FAILED: ", err, 'danger');
            enableUpdateUser();
        }

        if (deletingOurselves) {
            window.location.reload();
        }
    }

    function resetRegularUserPasswordFields() {
        setCurrentPassword('');
        setPassword('');
        setPasswordConfirm('');
    }

    async function updatePasswordRegular() {
        const current_password = currentPassword;
        const new_password = password;
        const new_password_confirm = passwordConfirm;
        resetRegularUserPasswordFields();

        if (new_password !== new_password_confirm) {
            showTemporaryMessage("ERROR", "New passwords don't match!", 'danger');
            enableUpdateUser();
            return;
        }

        if (new_password === "") {
            showTemporaryMessage("ERROR", "Can't have an empty password!", 'danger');
            enableUpdateUser();
            return;
        }

        const request_body = {
            current_password: current_password,
            password: new_password
        };

        try {
            const res = await update_user(loginToken, userData.id, request_body);
            if (res.success) {
                showTemporaryMessage(t("password_successfully_updated"));
                enableUpdateUser();
            } else {
                showTemporaryMessage("Password update FAILED", res.msg, 'danger');
                enableUpdateUser();
            }
        } catch (err) {
            showTemporaryMessage("Password update FAILED", err.message, 'danger');
            enableUpdateUser();
        }
    }

    return (
        <>
            <div className="w-full">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="flex flex-col">
                            <div className="flex flex-col md:flex-row flex-wrap w-full align-top justify-between default user-details">
                                {userData ? (
                                    <>
                                        <div className="flex-wrap neumorp max-w-lg">
                                            <h2 className="text-2xl text-center font-bold mb-1">{t("user_details")}</h2>

                                            <form>
                                                <div className="flex flex-col md:flex-row gap-3 text-sm leading-4 text-gray-700 mb-3">
													{/* Name; only allow update for admin users */}
                                                    {isAdminUser ? (
                                                        <div className="flex items-center w-full">
                                                            <label htmlFor="name" className="text-sm font-bold leading-4 text-gray-700 mr-1 text-nowrap">
																{t("name")}:
                                                            </label>
                                                            <input
                                                                disabled={!submitGuiEnabled}
                                                                type="text"
                                                                id="user-detail-name"
                                                                value={name}
                                                                name="name"
                                                                onChange={(e) => {
                                                                    setName(e.target.value);
                                                                }}
                                                                className="block rounded-md border border-gray-300 pl-1 py-1 w-full text-gray-900 shadow-sm focus-within:ring-1 focus-within:ring-inset focus-within:ring-cyan-600 sm:max-w-md sm:text-sm sm:leading-4"
                                                            />

                                                            {isAdminUser && (
                                                                <div className="flex items-center ml-5">
                                                                    <span className="font-bold text-nowrap">
                                                                        ID: {userData.id}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center">
                                                            <span className="text-sm font-bold leading-4 text-gray-700 mr-1 text-nowrap">
																{t("name")}:
                                                            </span>
                                                            <span id="user-detail-name" className="block w-full px-2 py-1 text-gray-900 sm:max-w-md sm:text-sm leading-4">
                                                                {name}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

												{/* Update password for admin users; that is don't ask for previous password or confirm new password */}
                                                {!isGoogleUser && !isBankIdUser && isAdminUser && (
                                                    <div className="flex flex-wrap w-full items-center mt-2 gap-1">
                                                        <label htmlFor="password" className="text-sm font-bold leading-4 text-gray-700 mr-2 text-nowrap min-w-[100px]">
                                                            New password:
                                                        </label>
                                                        <input
                                                            disabled={!submitGuiEnabled}
                                                            type="password"
                                                            id="user-detail-new-password-admin"
                                                            value={password}
                                                            onChange={(e) => {
                                                                setPassword(e.target.value);
                                                            }}
                                                            className="block flex-grow rounded-md border border-gray-300 px-2 py-1 text-gray-900 shadow-sm focus-within:ring-1 focus-within:ring-inset focus-within:ring-cyan-600 sm:max-w-md sm:text-sm leading-4"
                                                        />
                                                    </div>
                                                )}
                                            </form>

											{/* Toggle update password for regular users; only show for user/password users (that is, non-Google or BankID users) */}
                                            {!isGoogleUser && !isBankIdUser && !isAdminUser && !updatePasswordEnabledForRegularUser && (
                                                <div className="flex flex-wrap w-full items-center mt-2 gap-1">
                                                    <div id="update-password-enable" className="w-full">
                                                        <button
                                                            onClick={() => {
                                                                setUpdatePasswordEnabledForRegularUser(true);
                                                            }}
                                                            className="outline outline-2 outline-cyan-600 w-full text-cyan-600 font-semibold px-2 py-1 rounded-md text-center text-sm disabled:bg-gray-400 disabled:cursor-not-allowed transition ease-in-out hover:-translate-y-0.5 hover:scale-105 hover:bg-cyan-600 hover:text-white hover:outline-none"
                                                        >
															{t("update_password")}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

											{/* Update password for regular users */}
                                            {!isAdminUser && updatePasswordEnabledForRegularUser && (
                                                <form>
                                                    <div className="flex flex-col w-full items-start border-t mt-3 py-4 border-cyan-500 gap-2">
                                                        <div className="flex w-full justify-start">
                                                            <h4 className="text-lg font-bold">Change password</h4>
                                                        </div>
                                                        <div className="flex flex-wrap w-full items-center mt-2 gap-1">
                                                            <label htmlFor="password" className="text-sm font-bold leading-4 text-gray-700 mr-2 text-nowrap min-w-[150px]">
                                                                Current password:
                                                            </label>
                                                            <input
                                                                disabled={!submitGuiEnabled}
                                                                type="password"
                                                                id="user-detail-current-password"
                                                                value={currentPassword}
                                                                onChange={(e) => {
                                                                    setCurrentPassword(e.target.value);
                                                                }}
                                                                className="block flex-grow rounded-md border border-gray-300 px-2 py-1 text-gray-900 shadow-sm focus-within:ring-1 focus-within:ring-inset focus-within:ring-cyan-600 sm:max-w-md sm:text-sm leading-4"
                                                            />
                                                        </div>

                                                        <div className="flex flex-wrap w-full items-center mt-2 gap-1">
                                                            <label htmlFor="password" className="text-sm font-bold leading-4 text-gray-700 mr-2 text-nowrap min-w-[100px]">
                                                                New password:
                                                            </label>
                                                            <input
                                                                disabled={!submitGuiEnabled}
                                                                type="password"
                                                                id="user-detail-new-password-regular"
                                                                value={password}
                                                                onChange={(e) => {
                                                                    setPassword(e.target.value);
                                                                }}
                                                                className="block flex-grow rounded-md border border-gray-300 px-2 py-1 text-gray-900 shadow-sm focus-within:ring-1 focus-within:ring-inset focus-within:ring-cyan-600 sm:max-w-md sm:text-sm leading-4"
                                                            />
                                                        </div>
                                                        <div className="flex flex-wrap w-full items-center mt-2 gap-1">
                                                            <label htmlFor="password" className="text-sm font-bold leading-4 text-gray-700 mr-2 text-nowrap min-w-[100px]">
                                                                New password confirm:
                                                            </label>
                                                            <input
                                                                disabled={!submitGuiEnabled}
                                                                type="password"
                                                                id="user-detail-new-password-regular-confirm"
                                                                value={passwordConfirm}
                                                                onChange={(e) => {
                                                                    setPasswordConfirm(e.target.value);
                                                                }}
                                                                className="block flex-grow rounded-md border border-gray-300 px-2 py-1 text-gray-900 shadow-sm focus-within:ring-1 focus-within:ring-inset focus-within:ring-cyan-600 sm:max-w-md sm:text-sm leading-4"
                                                            />
                                                        </div>

                                                        <div className="flex w-full items-center justify-between mt-3 gap-2 sm:gap-4">
                                                            <button
                                                                onClick={() => {
                                                                    updatePasswordRegular();
                                                                    setUpdatePasswordEnabledForRegularUser(false);
                                                                }}
                                                                id="user-detail-new-password-regular-confirm-button"
                                                                disabled={!updatePasswordRegularEnabled}
                                                                className="btn-secondary w-full text-white px-2 py-1 rounded-md leading-1 text-center text-sm disabled:bg-gray-400 disabled:cursor-not-allowed transition ease-in-out hover:-translate-y-0.5 hover:scale-105"
                                                            >
                                                                Submit password
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    resetRegularUserPasswordFields();
                                                                    setUpdatePasswordEnabledForRegularUser(false);
                                                                }}
                                                                className="w-auto px-3 py-1 text-center font-semibold text-base text-red-500 disabled:bg-gray-300 disabled:cursor-not-allowed hover:text-cyan-600"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                </form>
                                            )}

                                            <div className="flex flex-col md:flex-row w-full items-center justify-between mt-4 gap-4">
												{/* Update user type, only available to super users */}
                                                {loggedInUserType === 'SUPERUSER' && (
                                                    <div className="flex w-full items-center">
                                                        <label htmlFor="type" className="text-sm font-medium leading-4 text-gray-700 mr-1 text-nowrap">
                                                            Type:
                                                        </label>
                                                        <div className="w-full">
                                                            <Listbox
                                                                as="div"
                                                                value={userType}
                                                                onChange={setUserType}
                                                                disabled={!submitGuiEnabled}>
                                                                {({ open }) => (
                                                                    <>
                                                                        <div className="relative items-center">
                                                                            <Listbox.Button className="inline-flex justify-between gap-x-1.5 rounded-md bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none">
                                                                                <span>{userType}</span>
                                                                                <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
                                                                            </Listbox.Button>

                                                                            <Transition
                                                                                as={Fragment}
                                                                                enter="transition ease-out duration-100"
                                                                                enterFrom="transform opacity-0 scale-95"
                                                                                enterTo="transform opacity-100 scale-100"
                                                                                leave="transition ease-in duration-75"
                                                                                leaveFrom="transform opacity-100 scale-100"
                                                                                leaveTo="transform opacity-0 scale-95"
                                                                            >
                                                                                <Listbox.Options
                                                                                    className="absolute right-0 z-10 mt-1 origin-top-right rounded-md bg-white ring-black ring-opacity-4 text-gray-900 shadow-sm sm:max-w-md sm:text-sm leading-4"
                                                                                    style={{ display: open ? 'block' : 'none' }}
                                                                                >
                                                                                    {userTypes.map((typeOption) => (
                                                                                        <Listbox.Option key={typeOption} value={typeOption}>
                                                                                            {({ active }) => (
                                                                                                <div
                                                                                                    className={`${
                                                                                                        active ? 'bg-gray-100' : ''
                                                                                                    } cursor-pointer select-none relative py-2 pl-3 pr-9`}
                                                                                                >
                                                                                                    {typeOption}
                                                                                                </div>
                                                                                            )}
                                                                                        </Listbox.Option>
                                                                                    ))}
                                                                                </Listbox.Options>
                                                                            </Transition>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </Listbox>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="flex flex-col sm:flex-row items-start justify-start sm:items-center sm:justify-between w-full gap-4">
													{/* Toggle whether user is enabled; only available to admin users */}
                                                    {isAdminUser && (
	                                                    <div className="flex items-center">
	                                                        <label htmlFor="user-detail-isenabled" className="text-sm font-bold leading-6 text-gray-700 mr-1 text-nowrap">
																{t("enabled")}:
	                                                        </label>
	                                                        <input
	                                                            disabled={!submitGuiEnabled}
	                                                            type="checkbox"
	                                                            id="user-detail-isenabled"
	                                                            checked={isEnabled}
	                                                            onChange={(e) => {
	                                                                setIsEnabled(e.target.checked);
	                                                            }}
	                                                            name="isenabled"
	                                                            className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
	                                                        />
	                                                    </div>
														)}
                                                </div>
                                            </div>
											{/* Submit button; only enabled when there is anything to update and if a regular user is currently updating her password */}
											{ !updatePasswordEnabledForRegularUser && (
                                            <div className="flex items-center justify-between mt-3 gap-4">
                                                <button
                                                    id="submit-changes-button"
                                                    disabled={!submitButtonEnabled || !submitGuiEnabled}
                                                    onClick={submitUpdate}
                                                    className="btn-main w-full text-white px-2 py-1 rounded-md text-center text-sm disabled:bg-gray-400 disabled:text-grey-600 disabled:cursor-not-allowed transition ease-in-out hover:-translate-y-0.5 hover:scale-105"
                                                >
                                                    {t("submit_changes")}
                                                </button>
												{/* Delete user; only available for admin users */}
                                                <button
                                                    id="delete-user-button"
                                                    disabled={!submitGuiEnabled}
                                                    hidden={!isAdminUser}
                                                    onClick={deleteUser}
                                                    className="w-auto text-red-500 px-3 py-1 rounded-md text-center font-semibold text-sm text-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed hover:text-red-700"
                                                >
                                                    Delete user
                                                </button>
                                            </div>
											)}
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex w-full h-full items-center justify-center text-xl italic font-semibold text-purple loading-animation">
                                        Loading user details...
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UserDetails;
